/**
 * Guide Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between Guide TypeScript type and Firestore data.
 *
 * Schema-models @segmently/schema-models/auth (v1.16.0+) ожидает Date для
 * format: date-time полей (openapi-generator-cli typescript-fetch convention).
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp } from 'firebase/firestore';
import type { Guide } from '@segmently/schema-models/auth';

function removeUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj
      .filter(item => item !== undefined)
      .map(item => {
        if (typeof item === 'object' && item !== null) {
          return removeUndefined(item);
        }
        return item;
      }) as any;
  }
  if (typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => v !== undefined)
        .map(([k, v]) => {
          if (typeof v === 'object' && v !== null) {
            return [k, removeUndefined(v)];
          }
          return [k, v];
        })
    ) as Partial<T>;
  }
  return obj;
}

export const guideConverter: FirestoreDataConverter<Guide> = {
  toFirestore(guide: Guide): any {
    const result: any = { ...guide };

    if (result['createdAt'] instanceof Date) {
      result['createdAt'] = Timestamp.fromDate(result['createdAt']);
    } else if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = Timestamp.fromDate(new Date(result['createdAt']));
    }

    if (result['updatedAt'] instanceof Date) {
      result['updatedAt'] = Timestamp.fromDate(result['updatedAt']);
    } else if (typeof result['updatedAt'] === 'string') {
      result['updatedAt'] = Timestamp.fromDate(new Date(result['updatedAt']));
    }

    return removeUndefined(result);
  },

  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): Guide {
    const data = snapshot.data(options);
    const result: any = { ...data, id: snapshot.id };

    // Schema-models ожидает Date — конвертируем Firestore Timestamp / string ISO → JS Date
    if (result['createdAt']) {
      if (result['createdAt'].toDate) {
        result['createdAt'] = result['createdAt'].toDate();
      } else if (result['createdAt'].seconds !== undefined) {
        result['createdAt'] = new Timestamp(result['createdAt'].seconds, result['createdAt'].nanoseconds || 0).toDate();
      } else if (typeof result['createdAt'] === 'string') {
        result['createdAt'] = new Date(result['createdAt']);
      }
    }

    if (result['updatedAt']) {
      if (result['updatedAt'].toDate) {
        result['updatedAt'] = result['updatedAt'].toDate();
      } else if (result['updatedAt'].seconds !== undefined) {
        result['updatedAt'] = new Timestamp(result['updatedAt'].seconds, result['updatedAt'].nanoseconds || 0).toDate();
      } else if (typeof result['updatedAt'] === 'string') {
        result['updatedAt'] = new Date(result['updatedAt']);
      }
    }

    return result as Guide;
  }
};

export default guideConverter;
